import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faBookOpen,
  faMagnifyingGlass,
  faCompressAlt,
  faExpandAlt,
  faChevronRight,
  faChevronDown,
  faEye,
  faPen,
  faUsers,
  faTimes,
  faSave,
  faTrash,
  faLayerGroup,
  faTriangleExclamation,
  faArrowDown,
  faLink,
  faStar,
  faCheck,
  faShareAlt,
  faTag,
  faUserMinus,
  faLevelUpAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../../components/context/AuthProvider";
import { useToast } from "../../../components/ui/Toast";
import { CampaignAssetService } from "../../../services/campaignAssetService";
import {
  CampaignAsset,
  AssetTableItem,
  AddAssetToCampaignRequest,
  CampaignAssetAccessRequest,
  CampaignAssetSection,
  CampaignAssetDiceTemplate,
  CampaignView,
} from "../../../types/types";
import { LoadingState, EmptyState, ConfirmationDialog } from "../../../components/ui";
import AssetMarkdownField from "../../assets/explorer/AssetMarkdownField";
import { getTypeConfig } from "../../assets/explorer/typeConfig";
import { collectAllIds, nodeMatchesFilter } from "../../assets/explorer/utils";
import CreateCampaignAssetModal from "./CreateCampaignAssetModal";
import DiceTemplateManager from "../../../components/business/DiceTemplate/DiceTemplateManager";
import DmNotesField from "../../../components/business/DmNotesField";

// ── Types ─────────────────────────────────────────────────────────────────────

interface CampaignAssetsProps {
  campaign: CampaignView;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function isLibraryAsset(ca: CampaignAsset): boolean {
  return ca.assetId !== 0;
}

interface TreeNode extends CampaignAsset {
  _children: TreeNode[];
}

function buildTree(assets: CampaignAsset[]): TreeNode[] {
  const map = new Map<number, TreeNode>();
  assets.forEach((a) => map.set(a.id, { ...a, _children: [] }));
  const roots: TreeNode[] = [];
  assets.forEach((a) => {
    if (a.parentId) {
      const parent = map.get(a.parentId);
      if (parent) {
        parent._children.push(map.get(a.id)!);
        return;
      }
    }
    roots.push(map.get(a.id)!);
  });
  return roots;
}

function filterTree(nodes: TreeNode[], q: string): TreeNode[] {
  if (!q) return nodes;
  const lq = q.toLowerCase();
  const result: TreeNode[] = [];
  for (const n of nodes) {
    const tags = Array.isArray(n.asset.tags) ? n.asset.tags : [];
    const match =
      n.asset.name.toLowerCase().includes(lq) ||
      n.asset.type.toLowerCase().includes(lq) ||
      (n.asset.category?.toLowerCase().includes(lq) ?? false) ||
      tags.some((t) => t.toLowerCase().includes(lq));
    const childMatches = filterTree(n._children, q);
    if (match || childMatches.length > 0) result.push({ ...n, _children: childMatches });
  }
  return result;
}

function countDescendants(node: TreeNode): number {
  return node._children.reduce((s, c) => s + 1 + countDescendants(c), 0);
}

/** Convert TreeNode[] to AssetTableItem[] (id = ca.id) for collectAllIds. */
function treeNodesToTableItems(nodes: TreeNode[]): AssetTableItem[] {
  return nodes.map((n) => ({
    ...n.asset,
    id: n.id,
    children: treeNodesToTableItems(n._children),
  }));
}

/** Flat AssetTableItem[] (id = ca.id) for AssetMarkdownField autocomplete. */
function campaignAssetsToTableItems(campaignAssets: CampaignAsset[]): AssetTableItem[] {
  return campaignAssets.map((ca) => ({ ...ca.asset, id: ca.id, children: undefined }));
}

// ── Component ─────────────────────────────────────────────────────────────────

const CampaignAssets: React.FC<CampaignAssetsProps> = ({ campaign }) => {
  const campaignId: number = campaign.id;
  const characters = campaign.characters || [];
  const auth = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const currentUserId = parseInt(auth.get("userId") ?? "0", 10);
  const canEditFormulas = currentUserId === campaign.createdByUserId;

  // ── Data state ──────────────────────────────────────────────────────────────
  const [campaignAssets, setCampaignAssets] = useState<CampaignAsset[]>([]);
  const [userAssets, setUserAssets] = useState<AssetTableItem[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Tree state ──────────────────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const [treeSearch, setTreeSearch] = useState("");
  const [treeDrawerOpen, setTreeDrawerOpen] = useState(false);
  // Close the mobile tree drawer whenever the selected entry changes.
  useEffect(() => {
    setTreeDrawerOpen(false);
  }, [selectedId]);

  // ── Library panel state ─────────────────────────────────────────────────────
  const [showLibrary, setShowLibrary] = useState(false);
  const [libSearch, setLibSearch] = useState("");
  const [includeChildren, setIncludeChildren] = useState(false);
  const [draggedAsset, setDraggedAsset] = useState<AssetTableItem | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loadingLib, setLoadingLib] = useState(false);
  const [expandedLib, setExpandedLib] = useState<Set<number>>(new Set());

  // ── Modals ──────────────────────────────────────────────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [accessAsset, setAccessAsset] = useState<CampaignAsset | null>(null);
  const [accessSelectedChars, setAccessSelectedChars] = useState<number[]>([]);
  const [accessSectionMap, setAccessSectionMap] = useState<Record<number, number[]>>({});
  const [expandedAccessSections, setExpandedAccessSections] = useState<Set<number>>(new Set());
  const [savingAccess, setSavingAccess] = useState(false);

  // ── Reference warning state ─────────────────────────────────────────────────
  const [refWarning, setRefWarning] = useState<{
    asset: AssetTableItem;
    missing: AssetTableItem[];
    withChildren: boolean;
  } | null>(null);
  const [processingRef, setProcessingRef] = useState(false);

  // ── Confirmation dialog ─────────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    variant?: "danger" | "warning" | "info";
    confirmText?: string;
    cancelText?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    onCancel: () => {},
  });

  // ── Tree drag-and-drop state ─────────────────────────────────────────────────
  const [draggedNodeId, setDraggedNodeId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const [dropOverRoot, setDropOverRoot] = useState(false);

  // ── Derived values ──────────────────────────────────────────────────────────
  const selectedAsset = campaignAssets.find((a) => a.id === selectedId) ?? null;
  const tree = buildTree(campaignAssets);
  const visibleTree = treeSearch.trim() ? filterTree(tree, treeSearch) : tree;

  // ── Data fetching ───────────────────────────────────────────────────────────
  const settleReloadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearScheduledReload = useCallback(() => {
    if (settleReloadTimeoutRef.current !== null) {
      clearTimeout(settleReloadTimeoutRef.current);
      settleReloadTimeoutRef.current = null;
    }
  }, []);

  const reload = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = opts?.silent ?? false;
      try {
        if (!silent) setLoading(true);
        const data = await CampaignAssetService.getCampaignAssetsHierarchy(campaignId);
        setCampaignAssets(data);
      } catch (err) {
        console.error("Failed to load campaign assets:", err);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [campaignId]
  );

  const scheduleSilentReload = useCallback(() => {
    clearScheduledReload();
    settleReloadTimeoutRef.current = setTimeout(() => {
      void reload({ silent: true });
      settleReloadTimeoutRef.current = null;
    }, 600);
  }, [clearScheduledReload, reload]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    return () => clearScheduledReload();
  }, [clearScheduledReload]);

  const openLibrary = async () => {
    setShowLibrary(true);
    // Always refetch — assets created elsewhere in the session must show up
    // without a full page reload.
    setLoadingLib(true);
    try {
      const data = await CampaignAssetService.getAssetsHierarchy();
      setUserAssets(data);
    } catch (err) {
      console.error("Failed to load user assets:", err);
    } finally {
      setLoadingLib(false);
    }
  };

  // ── Tree interactions ───────────────────────────────────────────────────────
  const toggleNode = (id: number) => {
    setExpandedNodes((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleLib = (id: number) => {
    setExpandedLib((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const expandAll = () => setExpandedNodes(collectAllIds(treeNodesToTableItems(tree)));
  const collapseAll = () => setExpandedNodes(new Set());

  // ── Library add/drag ────────────────────────────────────────────────────────
  const isInCampaign = (assetId: number) => campaignAssets.some((ca) => ca.assetId === assetId);

  const doAddAsset = async (
    asset: AssetTableItem,
    withChildren: boolean,
    added: Set<number> = new Set()
  ): Promise<void> => {
    // ponytail: local Set guards against re-adding an asset already handled in
    // this batch (e.g. a child that is also a missing reference). React state
    // updates are batched/stale mid-loop, so isInCampaign() alone is not sufficient.
    if (added.has(asset.id) || isInCampaign(asset.id)) return;
    added.add(asset.id);
    const req: AddAssetToCampaignRequest = { assetId: asset.id, characterAccess: [] };
    const newCampaignAsset = await CampaignAssetService.addAssetToCampaign(campaignId, req);
    // Optimistically push the new entry into local state so the sidebar
    // updates immediately without waiting for the full hierarchy reload.
    setCampaignAssets((prev) => [...prev, newCampaignAsset]);
    if (withChildren && asset.children?.length) {
      for (const child of asset.children) await doAddAsset(child, true, added);
    }
  };

  const handleAddLibraryAsset = async (asset: AssetTableItem) => {
    try {
      const validation = await CampaignAssetService.validateAssetForCampaign(campaignId, asset.id);
      if (!validation.canAdd && validation.missingAssets.length > 0) {
        setRefWarning({ asset, missing: validation.missingAssets, withChildren: includeChildren });
        return;
      }
      await doAddAsset(asset, includeChildren);
      showToast({ type: "success", message: `"${asset.name}" added to campaign` });
      // Settle-delay silent reload: catches any DB-lag where the hierarchy read
      // returns before all writes are fully visible, without flashing the page loader.
      scheduleSilentReload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to add asset";
      if (msg.includes("409") || msg.toLowerCase().includes("already")) {
        showToast({ type: "warning", message: `"${asset.name}" is already in the campaign` });
      } else {
        showToast({ type: "error", message: msg });
      }
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!draggedAsset) return;
    await handleAddLibraryAsset(draggedAsset);
    setDraggedAsset(null);
  };

  const handleConfirmRefWarning = async () => {
    if (!refWarning) return;
    setProcessingRef(true);
    try {
      const added = new Set<number>();
      for (const missing of refWarning.missing) await doAddAsset(missing, false, added);
      await doAddAsset(refWarning.asset, refWarning.withChildren, added);
      showToast({ type: "success", message: "Referenced assets imported successfully" });
      // Settle-delay silent reload: ensures all cascade inserts are reflected.
      scheduleSilentReload();
    } catch {
      showToast({ type: "error", message: "Failed to import some referenced assets" });
    } finally {
      setRefWarning(null);
      setProcessingRef(false);
    }
  };

  // ── Description save ────────────────────────────────────────────────────────
  const handleDescSave = async (value: string) => {
    if (!selectedAsset) return;
    await CampaignAssetService.updateCampaignAsset(campaignId, selectedAsset.id, {
      description: value,
    });
    await reload();
    showToast({ type: "success", message: "Description saved" });
  };

  // ── Section operations ──────────────────────────────────────────────────────
  const handleSectionSave = async (sectionId: number, name: string, content: string) => {
    if (!selectedAsset) return;
    // The PUT replaces the whole section — omitting dmNotes would blank the
    // stored DM notes, so carry the current value through.
    const section = (selectedAsset.sections ?? []).find((s) => s.id === sectionId);
    await CampaignAssetService.updateCampaignAssetSection(campaignId, selectedAsset.id, sectionId, {
      name,
      content,
      dmNotes: section?.dmNotes ?? "",
    });
    await reload();
    showToast({ type: "success", message: "Section saved" });
  };

  const handleSectionDelete = (sec: CampaignAssetSection) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Folio",
      message: `Delete folio "${sec.name}"? This cannot be undone.`,
      variant: "danger",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        setConfirmDialog((p) => ({ ...p, isOpen: false }));
        if (!selectedAsset) return;
        try {
          await CampaignAssetService.deleteCampaignAssetSection(
            campaignId,
            selectedAsset.id,
            sec.id
          );
          await reload();
          showToast({ type: "success", message: "Section deleted" });
        } catch {
          showToast({ type: "error", message: "Failed to delete folio" });
        }
      },
      onCancel: () => setConfirmDialog((p) => ({ ...p, isOpen: false })),
    });
  };

  const handleSectionCreate = async (name: string, content: string) => {
    if (!selectedAsset) return;
    await CampaignAssetService.createCampaignAssetSection(campaignId, selectedAsset.id, {
      name,
      content,
    });
    await reload();
    showToast({ type: "success", message: "Section added" });
  };

  // ── DM Notes save ────────────────────────────────────────────────────────────
  const handleDmNotesSave = async (dmNotes: string) => {
    if (!selectedAsset) return;
    await CampaignAssetService.updateCampaignAsset(campaignId, selectedAsset.id, { dmNotes });
    await reload();
  };

  const handleSectionDmNotesSave = async (
    sectionId: number,
    name: string,
    content: string,
    dmNotes: string
  ) => {
    if (!selectedAsset) return;
    await CampaignAssetService.updateCampaignAssetSection(campaignId, selectedAsset.id, sectionId, {
      name,
      content,
      dmNotes,
    });
    await reload();
  };

  // ── Remove asset ─────────────────────────────────────────────────────────────
  const handleRemoveAsset = (ca: CampaignAsset) => {
    setConfirmDialog({
      isOpen: true,
      title: "Remove Entry",
      message: `Remove "${ca.asset.name}" from the chronicle? This will also delete its folios and revelations.`,
      variant: "danger",
      confirmText: "Remove",
      cancelText: "Cancel",
      onConfirm: async () => {
        setConfirmDialog((p) => ({ ...p, isOpen: false }));
        try {
          await CampaignAssetService.removeAssetFromCampaign(campaignId, ca.id);
          if (selectedId === ca.id) setSelectedId(null);
          await reload();
          showToast({ type: "success", message: `"${ca.asset.name}" removed` });
        } catch {
          showToast({ type: "error", message: "Failed to remove entry" });
        }
      },
      onCancel: () => setConfirmDialog((p) => ({ ...p, isOpen: false })),
    });
  };

  // ── Access management ───────────────────────────────────────────────────────
  const openAccessModal = (ca: CampaignAsset) => {
    setAccessAsset(ca);
    const existingAccess = (ca.accessUsers ?? []).map((a) => a.characterId);
    const ownerCharIds = characters.filter((c) => c.userId === currentUserId).map((c) => c.id);
    // Pre-select owner chars only when no access has been configured yet
    setAccessSelectedChars(existingAccess.length > 0 ? existingAccess : ownerCharIds);

    const sectionMap: Record<number, number[]> = {};
    (ca.sections ?? []).forEach((s) => {
      sectionMap[s.id] = (s.accessUsers ?? []).map((a) => a.characterId);
    });
    setAccessSectionMap(sectionMap);
    setExpandedAccessSections(new Set());
  };

  const toggleAccessSection = (sectionId: number) => {
    setExpandedAccessSections((prev) => {
      const s = new Set(prev);
      s.has(sectionId) ? s.delete(sectionId) : s.add(sectionId);
      return s;
    });
  };

  const shareAllToSection = (sectionId: number) => {
    setAccessSectionMap((prev) => ({ ...prev, [sectionId]: characters.map((c) => c.id) }));
  };

  /** Save access using PUT (set semantics) — grants new, revokes removed. */
  const saveAccess = async () => {
    if (!accessAsset) return;
    setSavingAccess(true);
    try {
      const req: CampaignAssetAccessRequest = {
        assetAccess: accessSelectedChars.map((id) => ({ characterId: id })),
        sectionAccess: Object.entries(accessSectionMap).flatMap(([sid, cids]) =>
          cids.map((cid) => ({ characterId: cid, sectionId: Number(sid) }))
        ),
      };
      await CampaignAssetService.setAssetAccess(campaignId, accessAsset.id, req);
      await reload();
      setAccessAsset(null);
      showToast({ type: "success", message: "Access updated" });
    } catch {
      showToast({ type: "error", message: "Failed to update access" });
    } finally {
      setSavingAccess(false);
    }
  };

  /** Revoke a single character's access from the detail panel. */
  const handleRevokeCharacterAccess = async (characterId: number) => {
    if (!selectedAsset) return;
    try {
      const remainingChars = (selectedAsset.accessUsers ?? [])
        .map((a) => a.characterId)
        .filter((id) => id !== characterId);
      const remainingSectionAccess = (selectedAsset.sections ?? []).flatMap((s) =>
        (s.accessUsers ?? [])
          .filter((a) => a.characterId !== characterId)
          .map((a) => ({ characterId: a.characterId, sectionId: s.id }))
      );
      const req: CampaignAssetAccessRequest = {
        assetAccess: remainingChars.map((id) => ({ characterId: id })),
        sectionAccess: remainingSectionAccess,
      };
      await CampaignAssetService.setAssetAccess(campaignId, selectedAsset.id, req);
      await reload();
      showToast({ type: "success", message: "Access revoked" });
    } catch {
      showToast({ type: "error", message: "Failed to revoke access" });
    }
  };

  // ── Tree drag-and-drop helpers ──────────────────────────────────────────────
  /** Returns true if `checkId` is equal to or a descendant of `node`. */
  const isDescendantOf = useCallback((node: TreeNode, checkId: number): boolean => {
    if (node.id === checkId) return true;
    return node._children.some((c) => isDescendantOf(c, checkId));
  }, []);

  /** Returns true if `targetId` should be rejected as a drop target for `draggedId`. */
  const isInvalidDrop = useCallback(
    (draggedId: number, targetId: number): boolean => {
      if (draggedId === targetId) return true;
      const dragged = tree.reduce<TreeNode | null>((found, n) => {
        if (found) return found;
        const search = (node: TreeNode): TreeNode | null => {
          if (node.id === draggedId) return node;
          for (const c of node._children) {
            const r = search(c);
            if (r) return r;
          }
          return null;
        };
        return search(n);
      }, null);
      return dragged ? isDescendantOf(dragged, targetId) : false;
    },
    [tree, isDescendantOf]
  );

  const handleReparent = useCallback(
    async (assetId: number, newParentId: number | null) => {
      try {
        await CampaignAssetService.reparentAsset(campaignId, assetId, newParentId);
        await reload();
        // Expand the new parent so the moved node is visible
        if (newParentId !== null) {
          setExpandedNodes((prev) => new Set([...prev, newParentId]));
        }
        showToast({ type: "success", message: "Asset moved" });
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to move asset";
        showToast({ type: "error", message: msg });
      } finally {
        setDraggedNodeId(null);
        setDropTargetId(null);
        setDropOverRoot(false);
      }
    },
    [campaignId, reload, showToast]
  );

  // ── Library tree helpers ────────────────────────────────────────────────────
  const renderLibTree = (assets: AssetTableItem[], depth = 0): React.ReactNode =>
    assets
      .filter((a) => nodeMatchesFilter(a, libSearch))
      .map((asset) => {
        const inCampaign = isInCampaign(asset.id);
        const hasChildren = (asset.children?.length ?? 0) > 0;
        const isExpanded = expandedLib.has(asset.id);
        // Search reveals matching descendants even while collapsed (mirrors campaign tree)
        const showChildren = hasChildren && (isExpanded || libSearch.trim().length > 0);
        const libCfg = getTypeConfig(asset.type);
        const dotColor = libCfg.color.replace("text-", "bg-");
        return (
          <div key={asset.id} style={{ paddingLeft: `${depth * 16}px` }}>
            <div
              className={`flex items-center gap-2 px-2 py-1.5 rounded-sm ${
                inCampaign ? "opacity-45 cursor-not-allowed" : "hover:bg-amber-400/6 cursor-pointer"
              }`}
              draggable={!inCampaign}
              onDragStart={(e) => {
                if (!inCampaign) {
                  setDraggedAsset(asset);
                  e.dataTransfer.effectAllowed = "copy";
                }
              }}
              onDragEnd={() => {
                // Clear on any drag end, not just a successful drop, so an
                // abandoned drag does not leave stale state behind.
                setDraggedAsset(null);
                setIsDragOver(false);
              }}
            >
              {hasChildren ? (
                <button
                  type="button"
                  aria-label={isExpanded ? "Collapse" : "Expand"}
                  className="w-3 flex justify-center text-zinc-500 hover:text-zinc-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLib(asset.id);
                  }}
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronDown : faChevronRight}
                    className="text-xs"
                  />
                </button>
              ) : (
                <span className="w-3 flex justify-center">
                  <span className={`w-1.5 h-1.5 rounded-full ${dotColor} inline-block`} />
                </span>
              )}
              <FontAwesomeIcon
                icon={libCfg.icon}
                className={`${libCfg.color} text-xs w-4 text-center`}
              />
              <span className="text-sm flex-1 truncate">{asset.name}</span>
              {inCampaign ? (
                <span className="text-xs text-zinc-600 italic">added</span>
              ) : (
                <button
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-400 text-zinc-950 font-medium hover:bg-amber-300 transition-colors"
                  onClick={() => handleAddLibraryAsset(asset)}
                >
                  + Add
                </button>
              )}
            </div>
            {showChildren && renderLibTree(asset.children!, depth + 1)}
          </div>
        );
      });

  // ── Tree node renderer ──────────────────────────────────────────────────────
  const renderTreeNode = (node: TreeNode, depth = 0): React.ReactNode => {
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedId === node.id;
    const hasChildren = node._children.length > 0;
    const childCount = countDescendants(node);
    const isScratch = !isLibraryAsset(node);
    const nodeCfg = getTypeConfig(node.asset.type);
    const dotColor = nodeCfg.color.replace("text-", "bg-");

    const isDragging = draggedNodeId === node.id;
    const isDropTarget = dropTargetId === node.id;
    const invalid = draggedNodeId !== null && isInvalidDrop(draggedNodeId, node.id);

    return (
      <div key={node.id}>
        <div
          draggable
          onDragStart={(e) => {
            setDraggedNodeId(node.id);
            e.dataTransfer.effectAllowed = "move";
            // Store id in transfer so nested handlers can read it
            e.dataTransfer.setData("text/plain", String(node.id));
            e.stopPropagation();
          }}
          onDragEnd={() => {
            setDraggedNodeId(null);
            setDropTargetId(null);
            setDropOverRoot(false);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (draggedNodeId === null || invalid) {
              e.dataTransfer.dropEffect = "none";
              return;
            }
            e.dataTransfer.dropEffect = "move";
            setDropTargetId(node.id);
            setDropOverRoot(false);
          }}
          onDragLeave={(e) => {
            // Only clear when leaving this element, not entering a child
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setDropTargetId(null);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const id = parseInt(e.dataTransfer.getData("text/plain"), 10);
            if (!id || isInvalidDrop(id, node.id)) return;
            void handleReparent(id, node.id);
          }}
          className={`flex items-center gap-2 py-1.5 cursor-pointer rounded-sm transition-colors
            ${isDragging ? "opacity-40" : ""}
            ${isDropTarget && !invalid ? "ring-1 ring-amber-400 bg-amber-400/10" : ""}
            ${
              isSelected && !isDropTarget
                ? "bg-amber-400/15 border-l-2 border-amber-400"
                : "border-l-2 border-transparent"
            }
            ${!isSelected && !isDropTarget ? "hover:bg-amber-400/8" : ""}
          `}
          style={{ paddingLeft: `${8 + depth * 16}px`, paddingRight: "8px" }}
          onClick={() => {
            setSelectedId(node.id);
            if (hasChildren) toggleNode(node.id);
          }}
        >
          {hasChildren ? (
            <FontAwesomeIcon
              icon={isExpanded ? faChevronDown : faChevronRight}
              className="text-zinc-500 text-xs w-3 shrink-0"
            />
          ) : (
            <span className="w-3 flex justify-center shrink-0">
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor} inline-block`} />
            </span>
          )}
          <FontAwesomeIcon
            icon={nodeCfg.icon}
            className={`${nodeCfg.color} text-xs w-4 text-center shrink-0`}
          />
          <span className="text-sm truncate flex-1 min-w-0">{node.asset.name}</span>
          {hasChildren && childCount > 0 && (
            <span className="text-xs text-zinc-500 shrink-0">{childCount}</span>
          )}
          {isScratch && (
            <span className="text-xs px-1 py-0.5 rounded bg-violet-400/20 text-violet-300 border border-violet-400/35 tracking-wide shrink-0">
              ★
            </span>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>{node._children.map((child) => renderTreeNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <LoadingState
        variant="spinner"
        message="Loading campaign assets…"
        size="lg"
        fullHeight={false}
      />
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        className="-mx-6 -mb-6 flex overflow-hidden bg-zinc-950"
        style={{ height: "calc(100vh - 290px)", minHeight: "520px" }}
      >
        {/* LEFT — Campaign Asset Tree (inline on desktop, off-canvas drawer on mobile) */}
        <aside
          className={`bg-zinc-900 border-r border-zinc-800 flex flex-col overflow-hidden fixed inset-y-0 left-0 z-30 w-[85vw] max-w-xs transform transition-transform md:static md:z-auto md:w-80 md:min-w-[200px] md:shrink-0 md:transform-none ${
            treeDrawerOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0`}
        >
          <div className="px-3 py-2.5 border-b border-zinc-800 flex items-center gap-1.5">
            <div className="relative flex-1">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 text-xs pointer-events-none"
              />
              <input
                type="search"
                placeholder="Search chronicle entries…"
                className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 pl-7 pr-2 py-1.5 text-sm outline-none focus:border-amber-400 transition-colors"
                value={treeSearch}
                onChange={(e) => setTreeSearch(e.target.value)}
              />
            </div>
            <button
              title="Collapse all"
              className="p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors text-xs"
              onClick={collapseAll}
            >
              <FontAwesomeIcon icon={faCompressAlt} />
            </button>
            <button
              title="Expand all"
              className="p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors text-xs"
              onClick={expandAll}
            >
              <FontAwesomeIcon icon={faExpandAlt} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {visibleTree.length === 0 ? (
              <div className="text-zinc-600 text-xs text-center py-8">
                {treeSearch ? "No matching assets" : "No campaign assets yet"}
              </div>
            ) : (
              visibleTree.map((node) => renderTreeNode(node))
            )}
          </div>

          <div className="border-t border-zinc-800 p-2">
            {/* Root drop zone — only visible while a drag is active */}
            {draggedNodeId !== null && (
              <div
                className={`mb-2 flex items-center justify-center gap-1.5 py-2 text-xs border border-dashed rounded-sm transition-colors ${
                  dropOverRoot
                    ? "border-amber-400 bg-amber-400/10 text-amber-400"
                    : "border-zinc-600 text-zinc-500"
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDropOverRoot(true);
                  setDropTargetId(null);
                }}
                onDragLeave={() => setDropOverRoot(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = parseInt(e.dataTransfer.getData("text/plain"), 10);
                  if (!id) return;
                  void handleReparent(id, null);
                }}
              >
                <FontAwesomeIcon icon={faLevelUpAlt} className="text-xs" />
                Drop here to make root
              </div>
            )}
            <button
              className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs text-zinc-500 hover:text-amber-400 hover:bg-zinc-800 transition-colors"
              onClick={() => setShowCreateModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="text-xs" />
              Add root entry
            </button>
          </div>
        </aside>

        {treeDrawerOpen && (
          <div
            className="fixed inset-0 z-20 bg-black/50 md:hidden"
            onClick={() => setTreeDrawerOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="hidden md:block w-1 bg-zinc-800 hover:bg-amber-400/40 cursor-col-resize shrink-0 transition-colors" />

        {/* RIGHT — Detail panel */}
        <main className="flex-1 overflow-y-auto bg-zinc-950">
          <button
            type="button"
            onClick={() => setTreeDrawerOpen(true)}
            className="md:hidden flex items-center gap-2 px-3 py-2 text-sm text-amber-400 border-b border-zinc-800 bg-zinc-900 w-full"
            aria-label="Browse chronicle entries"
          >
            ☰ Entries
          </button>
          <div className="px-6 py-3 border-b border-zinc-800 flex items-center gap-3 bg-zinc-900">
            <div className="flex-1 flex items-center gap-2 text-sm text-zinc-400 min-w-0">
              <button
                className="text-xs text-zinc-500 hover:text-amber-400 transition-colors whitespace-nowrap"
                onClick={() => navigate(`/protected/campaigns/${campaignId}`)}
              >
                Chronicle
              </button>
              <FontAwesomeIcon icon={faChevronRight} className="text-zinc-600 text-xs" />
              <span className="text-zinc-200 truncate">
                {selectedAsset ? selectedAsset.asset.name : "Compendium"}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-800 text-zinc-300 border border-zinc-700 hover:border-zinc-500 transition-colors"
                onClick={openLibrary}
              >
                <FontAwesomeIcon icon={faBookOpen} className="text-xs" />
                Add from Compendium
              </button>
              <button
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-400 text-zinc-950 font-medium hover:bg-amber-300 transition-colors"
                onClick={() => setShowCreateModal(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                New Entry
              </button>
            </div>
          </div>

          <div className="p-6">
            {!selectedAsset ? (
              <EmptyState
                title="Select an entry"
                description="Choose an entry from the tree on the left to view its details, or create a new one."
                icon="folder"
                size="md"
                action={
                  <button
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-amber-400 text-zinc-950 font-medium hover:bg-amber-300 transition-colors"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Create First Entry
                  </button>
                }
              />
            ) : (
              <AssetDetailPanel
                key={selectedAsset.id}
                asset={selectedAsset}
                allCampaignAssets={campaignAssets}
                campaignId={campaignId}
                canEditFormulas={canEditFormulas}
                onView={() =>
                  navigate(`/protected/campaigns/${campaignId}/assets/${selectedAsset.id}`)
                }
                onEdit={() =>
                  navigate(`/protected/campaigns/${campaignId}/assets/${selectedAsset.id}/edit`)
                }
                onManageAccess={() => openAccessModal(selectedAsset)}
                onRemove={() => handleRemoveAsset(selectedAsset)}
                onSelectAsset={(id) => setSelectedId(id)}
                onDescSave={handleDescSave}
                onSectionSave={handleSectionSave}
                onSectionDelete={handleSectionDelete}
                onSectionCreate={handleSectionCreate}
                onDmNotesSave={handleDmNotesSave}
                onSectionDmNotesSave={handleSectionDmNotesSave}
                onRevokeCharacterAccess={handleRevokeCharacterAccess}
              />
            )}
          </div>
        </main>
      </div>

      {/* Library Panel */}
      {showLibrary && (
        <>
          <div className="fixed inset-0 bg-black/30 z-30" onClick={() => setShowLibrary(false)} />
          <div className="fixed inset-y-0 right-0 w-full sm:w-[480px] bg-zinc-900 border-l border-zinc-800 shadow-2xl z-40 flex flex-col">
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-3 shrink-0">
              <FontAwesomeIcon icon={faBookOpen} className="text-amber-400" />
              <h2 className="text-base font-medium text-white">Compendium</h2>
              <button
                className="ml-auto p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
                onClick={() => setShowLibrary(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="px-4 py-3 border-b border-zinc-800 space-y-2 shrink-0">
              <div className="relative">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 text-xs pointer-events-none"
                />
                <input
                  type="search"
                  placeholder="Search library assets…"
                  className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 pl-7 pr-2 py-1.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  value={libSearch}
                  onChange={(e) => setLibSearch(e.target.value)}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="accent-amber-500"
                  checked={includeChildren}
                  onChange={(e) => setIncludeChildren(e.target.checked)}
                />
                Include child entries when adding
              </label>
            </div>
            <div className="flex-1 overflow-y-auto px-2 py-2">
              {loadingLib ? (
                <div className="text-center text-zinc-500 text-sm py-8">Loading…</div>
              ) : userAssets.length === 0 ? (
                <div className="text-center text-zinc-600 text-xs py-8">
                  No personal entries found
                </div>
              ) : (
                renderLibTree(userAssets.filter((a) => !a.parentId))
              )}
            </div>
            <div className="px-4 pb-4 shrink-0">
              <div
                className={`border-2 border-dashed rounded-sm p-5 text-center text-sm transition-colors ${
                  isDragOver
                    ? "border-amber-400 bg-amber-400/6 text-amber-400"
                    : "border-zinc-700 text-zinc-500"
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
              >
                <FontAwesomeIcon icon={faArrowDown} className="text-xl mb-2 block mx-auto" />
                Drag entries here to add to chronicle
              </div>
            </div>
          </div>
        </>
      )}

      {/* Create modal */}
      {showCreateModal && (
        <CreateCampaignAssetModal
          campaignId={campaignId}
          existingCampaignAssets={campaignAssets}
          onCreated={async (newAsset) => {
            setShowCreateModal(false);
            await reload();
            setSelectedId(newAsset.id);
            showToast({ type: "success", message: `"${newAsset.asset.name}" created` });
          }}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Access modal */}
      {accessAsset && (
        <div className="fixed inset-0 bg-black/65 z-50 flex items-center justify-center">
          <div className="bg-zinc-900 border border-zinc-700 w-full max-w-lg p-6 shadow-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-5">
              <FontAwesomeIcon icon={faUsers} className="text-amber-400 text-lg" />
              <h2 className="text-base font-light text-white">
                Revelation — {accessAsset.asset.name}
              </h2>
              <button
                className="ml-auto p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
                onClick={() => setAccessAsset(null)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Asset-level access */}
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-amber-400 tracking-widest uppercase">
                Reveal to Heroes
              </p>
              <button
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-zinc-800 text-zinc-300 border border-zinc-700 hover:border-amber-400/50 hover:text-amber-300 transition-colors"
                onClick={() => setAccessSelectedChars(characters.map((c) => c.id))}
                title="Reveal to all heroes"
              >
                <FontAwesomeIcon icon={faShareAlt} className="text-xs" />
                Reveal to all
              </button>
            </div>
            <div className="bg-zinc-800 border border-zinc-700 p-3 space-y-2 mb-5">
              {characters.length === 0 && (
                <p className="text-sm text-zinc-500 text-center py-2">No heroes in the chronicle</p>
              )}
              {characters.map((char) => {
                const isOwnerChar = char.userId === currentUserId;
                const hasAccess = accessSelectedChars.includes(char.id);
                return (
                  <label key={char.id} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-amber-500"
                      checked={hasAccess}
                      onChange={() =>
                        setAccessSelectedChars((prev) =>
                          prev.includes(char.id)
                            ? prev.filter((id) => id !== char.id)
                            : [...prev, char.id]
                        )
                      }
                    />
                    <span className="text-sm text-zinc-200 flex-1">{char.name}</span>
                    {isOwnerChar && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-400/18 text-amber-300 border border-amber-400/30">
                        owner
                      </span>
                    )}
                    {hasAccess && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-teal-400/18 text-teal-300 border border-teal-400/30">
                        has revelation
                      </span>
                    )}
                  </label>
                );
              })}
            </div>

            {/* Section-level access */}
            {accessAsset.sections && accessAsset.sections.length > 0 && (
              <>
                <p className="text-xs font-semibold text-amber-400 tracking-widest uppercase mb-2">
                  Folio-level Revelation
                </p>
                <div className="space-y-2 mb-5">
                  {accessAsset.sections.map((sec) => {
                    const isExpanded = expandedAccessSections.has(sec.id);
                    const grantedCount = (accessSectionMap[sec.id] ?? []).length;
                    return (
                      <div key={sec.id} className="bg-zinc-800 border border-zinc-700">
                        <div className="flex items-center gap-2 px-3 py-2 cursor-pointer">
                          <button
                            className="text-zinc-500 hover:text-zinc-200 transition-colors"
                            onClick={() => toggleAccessSection(sec.id)}
                          >
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronDown : faChevronRight}
                              className="text-xs"
                            />
                          </button>
                          <span
                            className="text-sm font-medium text-zinc-300 flex-1 cursor-pointer"
                            onClick={() => toggleAccessSection(sec.id)}
                          >
                            {sec.name}
                          </span>
                          <span className="text-xs text-zinc-500 mr-2">
                            {grantedCount}/{characters.length}
                          </span>
                          <button
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-zinc-700 text-zinc-400 border border-zinc-600 hover:border-amber-400/50 hover:text-amber-300 transition-colors"
                            onClick={() => shareAllToSection(sec.id)}
                            title="Share this section to all characters"
                          >
                            <FontAwesomeIcon icon={faShareAlt} className="text-xs" />
                            Reveal to all
                          </button>
                        </div>
                        {isExpanded && (
                          <div className="px-3 pb-3 border-t border-zinc-700 pt-2 flex flex-wrap gap-x-4 gap-y-2">
                            {characters.map((char) => (
                              <label
                                key={char.id}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  className="accent-amber-500"
                                  checked={(accessSectionMap[sec.id] ?? []).includes(char.id)}
                                  onChange={() =>
                                    setAccessSectionMap((prev) => {
                                      const curr = prev[sec.id] ?? [];
                                      return {
                                        ...prev,
                                        [sec.id]: curr.includes(char.id)
                                          ? curr.filter((id) => id !== char.id)
                                          : [...curr, char.id],
                                      };
                                    })
                                  }
                                />
                                <span className="text-xs text-zinc-400">{char.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div className="flex gap-2 justify-end">
              <button
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-zinc-800 text-zinc-300 border border-zinc-700 hover:border-zinc-500 transition-colors"
                onClick={() => setAccessAsset(null)}
                disabled={savingAccess}
              >
                Cancel
              </button>
              <button
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-amber-400 text-zinc-950 font-medium hover:bg-amber-300 transition-colors disabled:opacity-60"
                onClick={saveAccess}
                disabled={savingAccess}
              >
                <FontAwesomeIcon icon={faSave} className="text-xs" />
                {savingAccess ? "Saving…" : "Reveal"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reference warning modal */}
      {refWarning && (
        <div className="fixed inset-0 bg-black/65 z-50 flex items-center justify-center">
          <div className="bg-zinc-900 border border-zinc-700 w-full max-w-md p-6 shadow-2xl mx-4">
            <div className="flex items-center gap-3 mb-4">
              <FontAwesomeIcon icon={faTriangleExclamation} className="text-amber-400 text-lg" />
              <h2 className="text-base font-light text-white">Weave Link Warning</h2>
            </div>
            <p className="text-sm text-zinc-300 mb-3">
              <strong className="text-white">"{refWarning.asset.name}"</strong> references{" "}
              {refWarning.missing.length} asset{refWarning.missing.length !== 1 ? "s" : ""} not yet
              in this chronicle:
            </p>
            <div className="bg-zinc-800 border border-zinc-700 p-3 mb-4 space-y-1.5 text-sm">
              {refWarning.missing.map((a) => {
                const warnCfg = getTypeConfig(a.type);
                return (
                  <div key={a.id} className="flex items-center gap-2 text-zinc-300">
                    <FontAwesomeIcon
                      icon={warnCfg.icon}
                      className={`${warnCfg.color} text-xs w-4 text-center`}
                    />
                    {a.name}
                    <span className="ml-auto text-xs px-2 py-0.5 bg-zinc-700 text-zinc-400 border border-zinc-600">
                      {a.type}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-sm text-zinc-400 mb-5">
              These linked entries will be automatically imported alongside{" "}
              <em>{refWarning.asset.name}</em> to maintain valid weave links.
            </p>
            <div className="flex flex-col gap-2">
              <button
                className="inline-flex items-center justify-center gap-2 w-full px-4 py-2 text-sm bg-amber-400 text-zinc-950 font-medium hover:bg-amber-300 transition-colors disabled:opacity-60"
                onClick={handleConfirmRefWarning}
                disabled={processingRef}
              >
                <FontAwesomeIcon icon={faLayerGroup} />
                {processingRef ? "Importing…" : "Add Linked Entries & This Entry"}
              </button>
              <button
                className="inline-flex items-center justify-center w-full px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
                onClick={() => setRefWarning(null)}
                disabled={processingRef}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
        variant={confirmDialog.variant}
      />
    </>
  );
};

// ── Asset Detail Panel ─────────────────────────────────────────────────────────

interface AssetDetailPanelProps {
  asset: CampaignAsset;
  allCampaignAssets: CampaignAsset[];
  campaignId: number;
  /** When true, the DM can create/update/delete dice templates on chronicle entries. */
  canEditFormulas: boolean;
  onView: () => void;
  onEdit: () => void;
  onManageAccess: () => void;
  onRemove: () => void;
  onSelectAsset: (_id: number) => void;
  onDescSave: (_value: string) => Promise<void>;
  onSectionSave: (_sectionId: number, _name: string, _content: string) => Promise<void>;
  onSectionDelete: (_sec: CampaignAssetSection) => void;
  onSectionCreate: (_name: string, _content: string) => Promise<void>;
  onDmNotesSave: (_dmNotes: string) => Promise<void>;
  onSectionDmNotesSave: (
    _sectionId: number,
    _name: string,
    _content: string,
    _dmNotes: string
  ) => Promise<void>;
  onRevokeCharacterAccess: (_characterId: number) => void;
}

const AssetDetailPanel: React.FC<AssetDetailPanelProps> = ({
  asset,
  allCampaignAssets,
  campaignId,
  canEditFormulas,
  onView,
  onEdit,
  onManageAccess,
  onRemove,
  onSelectAsset,
  onDescSave,
  onSectionSave,
  onSectionDelete,
  onSectionCreate,
  onDmNotesSave,
  onSectionDmNotesSave,
  onRevokeCharacterAccess,
}) => {
  const isScratch = !isLibraryAsset(asset);
  const cfg = getTypeConfig(asset.asset.type);
  const tags = Array.isArray(asset.asset.tags) ? asset.asset.tags : [];

  // Campaign assets as flat AssetTableItem[] (id = ca.id) for AssetMarkdownField
  const assetItems = campaignAssetsToTableItems(allCampaignAssets);

  // ── Local state ─────────────────────────────────────────────────────────────
  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(
    () => new Set((asset.sections ?? []).map((s) => s.id))
  );
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [sectionNameDraft, setSectionNameDraft] = useState<string>("");
  const [addingSectionOpen, setAddingSectionOpen] = useState(false);
  const [newSection, setNewSection] = useState({ name: "", content: "" });
  const [savingNewSection, setSavingNewSection] = useState(false);

  // Reset collapsed state when asset changes — start collapsed by default
  useEffect(() => {
    setCollapsedSections(new Set((asset.sections ?? []).map((s) => s.id)));
  }, [asset.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fate Formulas — editable for DM, read-only for others
  const supportsFateFormulas = (type: string) =>
    type === "Character" || type === "Beast" || type === "NPC";
  const [diceTemplates, setDiceTemplates] = useState<CampaignAssetDiceTemplate[]>([]);
  const [diceTemplatesLoading, setDiceTemplatesLoading] = useState(false);

  const loadDiceTemplates = useCallback(
    (assetId: number) => {
      setDiceTemplatesLoading(true);
      CampaignAssetService.getCampaignAssetDiceTemplates(campaignId, assetId)
        .then((data) => setDiceTemplates(data ?? []))
        .catch(() => setDiceTemplates([]))
        .finally(() => setDiceTemplatesLoading(false));
    },
    [campaignId]
  );

  useEffect(() => {
    if (!supportsFateFormulas(asset.asset.type)) {
      setDiceTemplates([]);
      return;
    }
    loadDiceTemplates(asset.id);
  }, [asset.id, asset.asset.type, loadDiceTemplates]);

  const parentAsset = asset.parentId
    ? (allCampaignAssets.find((ca) => ca.id === asset.parentId) ?? null)
    : null;
  const childAssets = allCampaignAssets.filter((ca) => ca.parentId === asset.id);

  const toggleSection = (id: number) => {
    setCollapsedSections((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const handleAddSection = async () => {
    if (!newSection.name.trim()) return;
    setSavingNewSection(true);
    try {
      await onSectionCreate(newSection.name, newSection.content);
      setAddingSectionOpen(false);
      setNewSection({ name: "", content: "" });
    } finally {
      setSavingNewSection(false);
    }
  };

  return (
    <div>
      {/* Asset header */}
      <div className="flex items-start gap-5 mb-6">
        <div className="w-16 h-16 bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0">
          <FontAwesomeIcon icon={cfg.icon} className={`${cfg.color} text-2xl`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h1 className="text-2xl font-light text-white tracking-wide">{asset.asset.name}</h1>
            {isScratch ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-400/18 text-violet-300 border border-violet-400/30">
                <FontAwesomeIcon icon={faStar} className="text-xs" /> Chronicle Only
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-400/18 text-blue-300 border border-blue-400/30">
                <FontAwesomeIcon icon={faLink} className="text-xs" /> From Compendium
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 mb-2">
            <span className={cfg.color}>{asset.asset.type}</span>
            <span className="text-zinc-600 mx-1.5">·</span>
            <span>{asset.asset.category}</span>
          </p>
          {tags.length > 0 && (
            <div className="flex gap-1.5 flex-wrap items-center">
              <FontAwesomeIcon icon={faTag} className="text-zinc-600 text-xs" />
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-400/18 text-amber-300 border border-amber-400/30"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        {/* Action buttons */}
        <div className="flex gap-2 shrink-0 flex-wrap justify-end">
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-800 text-zinc-300 border border-zinc-700 hover:border-zinc-500 transition-colors"
            onClick={onView}
          >
            <FontAwesomeIcon icon={faEye} className="text-xs" /> View
          </button>
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-400 text-zinc-950 font-medium hover:bg-amber-300 transition-colors"
            onClick={onEdit}
          >
            <FontAwesomeIcon icon={faPen} className="text-xs" /> Edit
          </button>
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-800 text-zinc-300 border border-zinc-700 hover:border-zinc-500 transition-colors"
            onClick={onManageAccess}
          >
            <FontAwesomeIcon icon={faUsers} className="text-xs" /> Revelation
          </button>
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-800 text-red-400 border border-zinc-700 hover:bg-red-400/15 hover:border-red-400 transition-colors"
            onClick={onRemove}
            title="Remove from chronicle"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      {/* Description */}
      <div className="mb-6">
        <AssetMarkdownField
          value={asset.asset.description ?? ""}
          label="DESCRIPTION"
          onSave={onDescSave}
          allAssets={assetItems}
          onReferenceNavigate={onSelectAsset}
        />
      </div>

      {/* DM Notes */}
      <div className="mb-6">
        <DmNotesField value={asset.dmNotes ?? ""} onSave={onDmNotesSave} />
      </div>

      {/* Sections */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-semibold text-amber-400 tracking-widest uppercase">
            Sections
          </h2>
          <div className="flex items-center gap-2">
            {(asset.sections ?? []).length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setCollapsedSections(new Set())}
                  className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  Expand All
                </button>
                <span className="text-zinc-700">|</span>
                <button
                  type="button"
                  onClick={() =>
                    setCollapsedSections(new Set((asset.sections ?? []).map((s) => s.id)))
                  }
                  className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  Collapse All
                </button>
                {!addingSectionOpen && <span className="text-zinc-700">|</span>}
              </>
            )}
            {!addingSectionOpen && (
              <button
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-800 text-zinc-300 border border-zinc-700 hover:border-zinc-500 transition-colors"
                onClick={() => setAddingSectionOpen(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" /> Add Folio
              </button>
            )}
          </div>
        </div>

        {(asset.sections ?? []).length === 0 && !addingSectionOpen && (
          <p className="text-zinc-600 text-sm italic">No folios yet.</p>
        )}

        {(asset.sections ?? []).map((sec) => {
          const isCollapsed = collapsedSections.has(sec.id);
          const isEditingName = editingSectionId === sec.id;

          return (
            <div
              key={sec.id}
              className="border border-zinc-800 mb-2 bg-zinc-900/40 hover:border-zinc-700 transition-colors"
            >
              {/* Section header */}
              <div
                className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                onClick={() => {
                  if (!isEditingName) toggleSection(sec.id);
                }}
              >
                <FontAwesomeIcon
                  icon={isCollapsed ? faChevronRight : faChevronDown}
                  className="text-zinc-500 text-xs shrink-0"
                />
                {isEditingName ? (
                  <input
                    type="text"
                    className="bg-zinc-800 border border-zinc-700 text-zinc-200 px-2 py-1 text-sm outline-none focus:border-amber-400 flex-1"
                    value={sectionNameDraft}
                    onChange={(e) => setSectionNameDraft(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span className="text-sm font-medium text-zinc-200 flex-1">{sec.name}</span>
                )}
                <div className="ml-auto flex gap-1 shrink-0">
                  {!isEditingName && (
                    <button
                      className="p-1 text-xs text-red-500 hover:text-red-400 hover:bg-zinc-800 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSectionDelete(sec);
                      }}
                      title="Delete folio"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              </div>

              {/* Section content via AssetMarkdownField */}
              {!isCollapsed && (
                <div className="px-3 pb-3 border-t border-zinc-800 pt-2">
                  <AssetMarkdownField
                    value={sec.content}
                    onSave={async (newContent) => {
                      const name = isEditingName ? sectionNameDraft : sec.name;
                      await onSectionSave(sec.id, name, newContent);
                      setEditingSectionId(null);
                    }}
                    onModeChange={(mode) => {
                      if (mode === "edit") {
                        setEditingSectionId(sec.id);
                        setSectionNameDraft(sec.name);
                        setCollapsedSections((prev) => {
                          const s = new Set(prev);
                          s.delete(sec.id);
                          return s;
                        });
                      } else {
                        setEditingSectionId(null);
                      }
                    }}
                    allAssets={assetItems}
                    onReferenceNavigate={onSelectAsset}
                  />
                  <div className="mt-2">
                    <DmNotesField
                      value={sec.dmNotes ?? ""}
                      onSave={(dmNotes) =>
                        onSectionDmNotesSave(sec.id, sec.name, sec.content, dmNotes)
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add section form */}
        {addingSectionOpen && (
          <div className="border border-zinc-700 p-3 bg-zinc-900/50 mt-2">
            <p className="text-xs font-semibold text-amber-400 tracking-widest uppercase mb-2">
              New Folio
            </p>
            <input
              type="text"
              className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 px-3 py-1.5 text-sm outline-none focus:border-amber-400 transition-colors mb-2"
              placeholder="Folio name…"
              value={newSection.name}
              onChange={(e) => setNewSection((p) => ({ ...p, name: e.target.value }))}
              autoFocus
            />
            <AssetMarkdownField
              value={newSection.content}
              onChange={(v) => setNewSection((p) => ({ ...p, content: v }))}
              allAssets={assetItems}
              onReferenceNavigate={onSelectAsset}
            />
            <div className="flex gap-2 mt-2">
              <button
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-400 text-zinc-950 font-medium hover:bg-amber-300 transition-colors disabled:opacity-60"
                onClick={handleAddSection}
                disabled={savingNewSection || !newSection.name.trim()}
              >
                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                {savingNewSection ? "Adding…" : "Add Folio"}
              </button>
              <button
                className="px-3 py-1.5 text-xs text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
                onClick={() => {
                  setAddingSectionOpen(false);
                  setNewSection({ name: "", content: "" });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Fate Formulas — editable for DM (canEditFormulas), read-only for others */}
      {supportsFateFormulas(asset.asset.type) && (
        <div className="mb-6">
          <DiceTemplateManager
            templates={diceTemplates.map((t) => ({
              id: t.id,
              characterId: t.campaignAssetId,
              name: t.name,
              diceNotation: t.diceNotation,
              modifier: t.modifier,
              description: t.description,
              skillType: t.skillType,
              createdAt: t.createdAt,
              updatedAt: t.updatedAt,
            }))}
            loading={diceTemplatesLoading}
            campaignId={campaignId}
            readOnly={!canEditFormulas}
            onCreateTemplate={async (req) => {
              await CampaignAssetService.createCampaignAssetDiceTemplate(campaignId, asset.id, req);
              loadDiceTemplates(asset.id);
            }}
            onUpdateTemplate={async (templateId, req) => {
              await CampaignAssetService.updateCampaignAssetDiceTemplate(
                campaignId,
                asset.id,
                templateId,
                req
              );
              loadDiceTemplates(asset.id);
            }}
            onDeleteTemplate={async (templateId) => {
              await CampaignAssetService.deleteCampaignAssetDiceTemplate(
                campaignId,
                asset.id,
                templateId
              );
              loadDiceTemplates(asset.id);
            }}
          />
        </div>
      )}

      {/* Hierarchy nav */}
      <div className="border-t border-zinc-800 pt-4 mb-6">
        <h2 className="text-xs font-semibold text-amber-400 tracking-widest uppercase mb-3">
          Hierarchy
        </h2>
        <p className="text-xs text-zinc-500 mb-2">Parent</p>
        {parentAsset ? (
          <button
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 hover:border-zinc-500 transition-colors mb-3"
            onClick={() => onSelectAsset(parentAsset.id)}
          >
            <FontAwesomeIcon
              icon={getTypeConfig(parentAsset.asset.type).icon}
              className={getTypeConfig(parentAsset.asset.type).color + " text-xs"}
            />
            {parentAsset.asset.name}
          </button>
        ) : (
          <p className="text-sm text-zinc-500 italic mb-3">No parent — root entry</p>
        )}
        {childAssets.length > 0 && (
          <>
            <p className="text-xs text-zinc-500 mb-2">Children ({childAssets.length})</p>
            <div className="flex flex-wrap gap-2">
              {childAssets.map((child) => {
                const childCfg = getTypeConfig(child.asset.type);
                return (
                  <button
                    key={child.id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 hover:border-zinc-500 transition-colors"
                    onClick={() => onSelectAsset(child.id)}
                  >
                    <FontAwesomeIcon icon={childCfg.icon} className={childCfg.color + " text-xs"} />
                    {child.asset.name}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Character access */}
      {(asset.accessUsers ?? []).length > 0 && (
        <div className="border-t border-zinc-800 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold text-amber-400 tracking-widest uppercase">
              Hero Revelation
            </h2>
            <button
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-800 text-zinc-300 border border-zinc-700 hover:border-zinc-500 transition-colors"
              onClick={onManageAccess}
            >
              <FontAwesomeIcon icon={faPen} className="text-xs" /> Manage
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {asset.accessUsers.map((access) => {
              const initial = (access.character?.name ?? "?")[0].toUpperCase();
              return (
                <div
                  key={access.characterId}
                  className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 px-3 py-1.5"
                >
                  <div className="w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center text-xs font-bold text-white">
                    {initial}
                  </div>
                  <span className="text-sm text-zinc-300">{access.character?.name}</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-teal-400/18 text-teal-300 border border-teal-400/30">
                    can view
                  </span>
                  <button
                    className="ml-1 p-0.5 text-zinc-600 hover:text-red-400 transition-colors"
                    title={`Revoke access for ${access.character?.name}`}
                    onClick={() => onRevokeCharacterAccess(access.characterId)}
                  >
                    <FontAwesomeIcon icon={faUserMinus} className="text-xs" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignAssets;
